import { useEffect, useMemo, useState } from "react";
import { useInputContext, useInputSetters, Components } from "leva/plugin";
import { useGlobalState } from "../hooks/useGlobalState";
import { BezierProps, InternalBezier } from "../types/bezier-types";
import {
	applyTool,
	loadCustomPresets,
	saveCustomPresets,
	loadFavoritePresets,
	saveFavoritePresets,
	savePresetOrdering,
} from "../utilities/layers";
import { PresetObj, ToolApplyRequest } from "../types/general-types";
import { StyledPanelLock } from "../styles/StyledPanelLock";
import TopSection from "./TopSection";
import { UITools } from "../types/context-types";
import { useNotifications } from "../context/NotificationProvider";
import { styled } from "@stitches/react";
import { PresetsRenderSections } from "./PresetsRenderSections";
import { buildToolPresets } from "../utilities/build-presets";
import IconLock from "./icons/IconLock";
import IconUnlock from "./icons/IconUnlock";

export function PresetsPanel() {
	const { addNotification } = useNotifications();
	const { value, label, displayValue, onUpdate, onChange, settings } =
		useInputContext<BezierProps>();
	const { dispatch, UIStatus, configSettings, synergy, persistUI } =
		useGlobalState();
	const [presetRows, setPresetRows] = useState<PresetObj[][]>([[]]);
	const [presetFavorites, setPresetFavorites] = useState<PresetObj[]>([]);
	const [presetSearchInput, setPresetSearchInput] = useState<string>("");

	function deleteCustomPreset(presetName: string): void {
		let res: JSX.Element[] = [];
		loadCustomPresets().then((customPresets) => {
			customPresets = JSON.parse(customPresets);
			//@ts-ignore
			res = customPresets.filter(
				(preset: PresetObj) => preset.name !== presetName
			);
			//@ts-ignore
			saveCustomPresets(res).then(() => {
				buildPresets();
			});
		});
	}

	function handleFavoriteClick(name: string): void {
		loadFavoritePresets().then((favorites) => {
			alert(favorites);
			let favoritesObj = JSON.parse(favorites);
			if (favoritesObj[name]) {
				favoritesObj[name] = false;
			} else {
				favoritesObj[name] = true;
			}
			saveFavoritePresets(favoritesObj).then(() => {
				buildPresets();
			});
		});
	}

	function buildPresets() {
		buildToolPresets(UITools.bezier, setPresetRows, setPresetFavorites);
	}

	function handleSynergySelect(name: string): void {
		if (synergy.getIn) {
			dispatch({
				type: "synergy",
				payload: {
					easeIn: name,
					getIn: false,
					easeOut: synergy.easeOut,
					getOut: false,
					display: true,
				},
			});
			dispatch({
				type: "UIStatusUpdate",
				payload: {
					id: Date.now(),
					currentDisplay: "distribute",
					initializationTime: Date.now(),
				},
			});
		} else if (synergy.getOut) {
			dispatch({
				type: "synergy",
				payload: {
					easeOut: name,
					getOut: false,
					easeIn: synergy.easeIn,
					getIn: false,
					display: true,
				},
			});
			dispatch({
				type: "UIStatusUpdate",
				payload: {
					id: Date.now(),
					currentDisplay: "distribute",
					initializationTime: Date.now(),
				},
			});
		}
	}

	//Sends value and UI update to global state
	const handleSVGClick = (value: [], name: string) => {
		if (synergy.getIn || synergy.getOut) {
			handleSynergySelect(name);
		} else if (persistUI && persistUI.presetPanelLocked!) {
			//@ts-ignore
			applyBezierHandler(value);
		} else {
			dispatch({
				type: "UIStatusUpdate",
				payload: {
					id: Date.now(),
					currentDisplay: synergy.display ? "distribute" : "bezier",
					bezierDisplayValue: value,
					initializationTime: Date.now(),
				},
			});
		}
	};

	const handleInputChange = (e: any) => {
		let query = e.target.value;
		setPresetSearchInput(query);
	};

	useEffect(() => {
		buildPresets();
	}, []);

	function handlePanelLockToggle(): void {
		if (persistUI) {
			dispatch({
				type: "persistUI",
				payload: {
					...persistUI,
					presetPanelLocked: !persistUI.presetPanelLocked,
				},
			});
		}
	}

	function handlePresetSectionUp(index: number): void {
		if (index === 0) return; // If already at the top, do nothing.
		let newSectionOrder: PresetObj[][] = [];
		setPresetRows((prevSectionOrder) => {
			newSectionOrder = [...prevSectionOrder]; // Create a new copy of the section order array.
			[newSectionOrder[index - 1], newSectionOrder[index]] = [
				newSectionOrder[index],
				newSectionOrder[index - 1],
			];
			return newSectionOrder;
		});
		let presetSectionOrder: string[] = [];
		newSectionOrder.forEach((section) => {
			presetSectionOrder.push(section[0].section);
		});
		savePresetOrdering(presetSectionOrder, "bezier");
	}

	return (
		<>
			{/* <TopSection handleInputChange={handleInputChange} /> */}
			<StyledRow>
				<StyledPanelLock onClick={handlePanelLockToggle}>
					{persistUI && persistUI.presetPanelLocked ? (
						<IconLock />
					) : (
						<IconUnlock />
					)}
				</StyledPanelLock>

				<PresetsRenderSections
					handlePresetSectionUp={handlePresetSectionUp}
					sectionArrays={presetRows}
					presetFavorites={presetFavorites}
					handleSVGClick={handleSVGClick}
					presetSearchInput={presetSearchInput}
					deleteCustomPreset={deleteCustomPreset}
					handleFavoriteClick={handleFavoriteClick}
				/>
			</StyledRow>
		</>
	);
}

const StyledRow = styled("div", {
	overflow: "auto",
	padding: "5px 15px",
	backgroundColor: "#181c20",
	display: "flex",
	flexDirection: "column",
	alignItems: "start",

	// WebKit-based browsers (Chrome, Safari, etc.)
	"&::-webkit-scrollbar": {
		width: "4px",
		height: "4px",
	},
	"&::-webkit-scrollbar-thumb": {
		backgroundColor: "rgba(96, 165, 250, 1)",
	},
	"&::-webkit-scrollbar-track": {
		backgroundColor: "transparent",
	},
});
